import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["trigger", "dropdown", "chevron"]
    static values = {
        position: { type: String, default: "bottom" },
        fixedWidth: { type: String, default: "260px" }
    }

    connect() {
        this.isOpen = false
        // Cache the element to avoid losing reference when moving
        this.menuElement = this.dropdownTarget
        // Create a placeholder to mark where the menu should return
        this.placeholder = document.createComment("dropdown-placeholder")
    }

    disconnect() {
        this.close()
        if (this.menuElement && document.body.contains(this.menuElement)) {
            this.menuElement.remove()
        }
    }

    toggle(event) {
        event.stopPropagation()
        event.preventDefault()
        if (this.isOpen) {
            this.close()
        } else {
            this.open()
        }
    }

    open() {
        if (this.isOpen) return
        this.isOpen = true

        // 1. Swap with placeholder and move to body
        this.menuElement.parentNode.insertBefore(this.placeholder, this.menuElement)
        document.body.appendChild(this.menuElement)

        // 2. Prepare styles
        this.menuElement.classList.remove("hidden")
        this.menuElement.style.position = 'fixed'
        this.menuElement.style.zIndex = '99999'
        // Prevent width collapse during move
        this.menuElement.style.width = 'max-content'

        // 3. Position accurately
        this.updatePosition()

        // 4. Animate
        requestAnimationFrame(() => {
            this.menuElement.classList.remove("opacity-0", "scale-95")
            this.menuElement.classList.add("opacity-100", "scale-100")
        })

        this.element.setAttribute("data-state", "open")
        if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "true")

        this.bindListeners()
    }

    close() {
        if (!this.isOpen) return
        this.isOpen = false

        // Animate out
        this.menuElement.classList.remove("opacity-100", "scale-100")
        this.menuElement.classList.add("opacity-0", "scale-95")

        setTimeout(() => {
            if (!this.isOpen) {
                this.menuElement.classList.add("hidden")

                // Return to original location
                if (this.placeholder.parentNode) {
                    this.placeholder.replaceWith(this.menuElement)
                }

                // Cleanup styles
                this.menuElement.style.position = ''
                this.menuElement.style.top = ''
                this.menuElement.style.left = ''
                this.menuElement.style.width = ''
                this.menuElement.style.bottom = ''
                this.menuElement.style.zIndex = ''
            }
        }, 100)

        this.element.removeAttribute("data-state")
        if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "false")
        this.unbindListeners()
    }

    updatePosition() {
        if (!this.isOpen) return

        const sidebar = document.getElementById('sidebar')
        const triggerRect = this.element.getBoundingClientRect()
        // Check collapse state
        const isCollapsed = sidebar && sidebar.getBoundingClientRect().width < 200

        const styles = {}

        if (isCollapsed) {
            // Collapsed
            styles.left = `${triggerRect.right + 10}px`
            styles.width = this.fixedWidthValue

            if (this.positionValue === 'top') {
                styles.top = 'auto'
                styles.bottom = `${window.innerHeight - triggerRect.bottom}px`
                styles.transformOrigin = 'bottom left'
            } else {
                styles.top = `${triggerRect.top}px`
                styles.bottom = 'auto'
                styles.transformOrigin = 'top left'
            }
        } else {
            // Expanded
            styles.left = `${triggerRect.left}px`
            styles.width = `${triggerRect.width}px` // Precise match

            if (this.positionValue === 'top') {
                styles.top = 'auto'
                styles.bottom = `${window.innerHeight - triggerRect.top + 6}px`
                styles.transformOrigin = 'bottom center'
            } else {
                styles.top = `${triggerRect.bottom + 6}px`
                styles.bottom = 'auto'
                styles.transformOrigin = 'top center'
            }
        }

        Object.assign(this.menuElement.style, styles)
    }

    bindListeners() {
        this.clickOutsideHandler = this.clickOutside.bind(this)
        this.escapeHandler = this.escape.bind(this)
        this.resizeHandler = this.updatePosition.bind(this)

        window.addEventListener("resize", this.resizeHandler)
        window.addEventListener("scroll", this.resizeHandler, true)
        document.addEventListener("click", this.clickOutsideHandler, true)
        document.addEventListener("keydown", this.escapeHandler)
    }

    unbindListeners() {
        window.removeEventListener("resize", this.resizeHandler)
        window.removeEventListener("scroll", this.resizeHandler, true)
        document.removeEventListener("click", this.clickOutsideHandler, true)
        document.removeEventListener("keydown", this.escapeHandler)
    }

    clickOutside(event) {
        if (!this.element.contains(event.target) && !this.menuElement.contains(event.target)) {
            this.close()
        }
    }

    escape(event) {
        if (event.key === "Escape") {
            this.close()
        }
    }
}
